<template>
  <FormGroup :inputId="idCalc" :errors="errorsCalc" :successes="successes">
    <slot name="label">
      <label
        :for="idCalc"
        v-if="label"
        class="tw-mb-2"
        :class="[
          {
            '!tw-text-text-disabled': disabled,
          },
          labelType,
        ]"
        v-html="label"
      />
    </slot>
    <div v-if="!loading" class="input-group">
      <slot name="prepend">
        <span
          v-if="prepend"
          class="input-group-text !tw-bg-background-strong !tw-border-border !tw-text-text"
          :class="[
            {
              'is-invalid': errorsCalc,
              'is-valid': successes,
              '!tw-text-text-disabled': disabled,
            },
            inputType,
          ]"
        >
          {{ prepend }}
        </span>
      </slot>
      <input
        ref="input"
        class="form-control !tw-px-4 !tw-py-2"
        :class="[{ 'is-invalid': errorsCalc, 'is-valid': successes }, inputType]"
        :data-test="testId"
        :id="idCalc"
        :name="name"
        :placeholder="placeholder"
        :type="type"
        :value="displayValue"
        :disabled="disabled"
        @input="emitInput"
        @change="emitChange"
        @keypress="keypress"
        :autocomplete="autocomplete"
        :tabindex="tabindex"
      />
      <slot name="append">
        <span
          v-if="append"
          class="input-group-text !tw-bg-background-strong !tw-border-border !tw-text-text"
        >
          {{ append }}
        </span>
      </slot>
    </div>
    <div v-else class="loading !tw-h-8" />
  </FormGroup>
</template>

<script>
import { computed, nextTick, ref } from "vue";
import FormGroup from "@/components/display/form/FormGroup";
import FormMixin from "@/components/display/form/FormMixin";

export default {
  name: "FormInput",
  components: {
    FormGroup,
  },
  mixins: [FormMixin],
  emits: ["change", "input", "update:modelValue"],
  props: {
    append: {
      type: String,
    },
    value: [Number, String],
    placeholder: [String],
    modelValue: [Number, String],
    prepend: {
      type: String,
    },
    type: {
      type: String,
      default: "text",
    },
    autocomplete: {
      type: String,
      default: "on",
      validator: (val) => {
        return ["on", "off"].includes(val);
      },
    },
    creditCard: {
      type: Boolean,
      default: false,
    },
    variant: String,
    loading: {
      type: Boolean,
      default: false,
    },
    testId: String,
  },
  setup(props, { emit }) {
    const input = ref(null);
    const lastInput = ref(null);
    const labelTypeList = {
      light: "",
    };
    const labelType = computed(() =>
      !labelTypeList[props.variant] ? labelTypeList.light : labelTypeList[props.variant]
    );
    const inputTypeList = {
      light: "",
    };
    const inputType = computed(() =>
      !inputTypeList[props.variant] ? inputTypeList.light : inputTypeList[props.variant]
    );

    const countSpaces = (text) => (text || "").split(" ")?.length - 1;
    const getSpaceIndexes = (text) => {
      const indexes = [];
      for (let i = 0; i < text?.length || 0; i++) {
        if (text[i] === " ") indexes.push(i);
      }
      return indexes;
    };
    const setCursorPosition = (element, position) => {
      element.focus();
      element.setSelectionRange(position, position);
    };

    const keypress = (e) => {
      if (props.creditCard) {
        const charCode = e.which || e.keyCode;
        if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
          e.preventDefault();
        }
      }
    };

    const creditCardFormat = (number) =>
      Number(number?.charAt(0)) === 3
        ? number?.replace(/^(.{4})(.*)$/, "$1 $2")?.replace(/^(.{11})(.*)$/, "$1 $2") // American Express
        : number // Other CCs
            ?.replace(/\W/gi, "")
            ?.replace(/(.{4})/g, "$1 ")
            ?.trim();

    const displayValue = computed(() => {
      if (props.creditCard) {
        const display = creditCardFormat(props.modelValue);
        let startPos = input.value?.selectionStart || 0;

        const currentSpaces = countSpaces(display);
        const currentLength = display?.length || 0;
        const lastSpaces = countSpaces(lastInput.value);
        const lastLength = lastInput.value?.length || 0;

        const spaceIndexes = getSpaceIndexes(display);

        if (
          (currentSpaces > lastSpaces && startPos === currentLength) ||
          (currentLength > lastLength && spaceIndexes.includes(startPos - 1))
        ) {
          startPos += 1;
        } else if (currentSpaces < lastSpaces && startPos !== 0) {
          startPos -= 1;
        }
        nextTick(() => setCursorPosition(input.value, startPos));
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        lastInput.value = display;
        return display;
      }
      return props.modelValue || props.value;
    });

    const emitValue = (value) =>
      props.creditCard ? value?.replace(/\s/g, "")?.replace(/\D/g, "") : value;

    const emitChange = (e) => emit("change", emitValue(e.target.value));
    const emitInput = (e) => {
      const value = emitValue(e.target.value.trim());
      emit("input", value);
      emit("update:modelValue", value);
    };

    return {
      displayValue,
      emitChange,
      emitInput,
      input,
      keypress,
      labelType,
      inputType,
    };
  },
};
</script>

<style lang="scss" scoped>
.password-strength-container {
  .password-help-tip {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    display: flex;
    flex-direction: row-reverse;

    .password-tip-text {
      transition: color 1s ease;
      font-size: 0.75rem;
      margin-right: 0.5rem;
    }
  }
  .password-strength {
    display: flex;
    flex-direction: row;
    height: 3px;
    margin-left: -5px;
    margin-right: -5px;
    margin-top: 5px;

    .password-strength-unit {
      flex: 0 0 25%;
      max-width: 25%;
      height: 3px;
      padding-right: 5px;
      padding-left: 5px;
      width: 100%;

      .password-strength-color {
        transition: background-color 1s ease;
        width: 100%;
        height: 100%;
      }
    }
  }
}
</style>
